import { useState } from 'react'
import type { InteractionProps } from './registry'
import { getNonEmptyString, getStringList } from './shared/config'
import { asRecord } from './shared/response'
import { StaticFallback } from './shared/StaticFallback'
import controls from './shared/controls.module.css'
import styles from './ShortResponse.module.css'

/**
 * Free response without automated grading. The learner writes an answer, then
 * reveals a model answer and self-checks against an idea checklist
 * (compare-and-revise). We never pretend to grade reasoning.
 */
export function ShortResponse({
  id,
  config,
  savedResponse,
  onResponseChange,
  onComplete,
}: InteractionProps) {
  const prompt = getNonEmptyString(config, 'prompt')
  const model = getNonEmptyString(config, 'model')
  const checklist = getStringList(config, 'checklist') ?? []

  const saved = asRecord(savedResponse)
  const [text, setText] = useState<string>(typeof saved.text === 'string' ? saved.text : '')
  const [revealed, setRevealed] = useState<boolean>(saved.revealed === true)
  const [checked, setChecked] = useState<string[]>(
    Array.isArray(saved.checked) ? (saved.checked as string[]) : [],
  )

  if (!prompt) {
    return <StaticFallback title="Short response">This prompt is unavailable.</StaticFallback>
  }

  const labelId = `${id}-prompt`

  function emit(next: { text?: string; revealed?: boolean; checked?: string[] }) {
    onResponseChange?.({ text, revealed, checked, ...next })
  }

  function onText(value: string) {
    setText(value)
    emit({ text: value })
  }

  function reveal() {
    setRevealed(true)
    emit({ revealed: true })
    onComplete?.()
  }

  function toggleItem(item: string) {
    const next = checked.includes(item)
      ? checked.filter((entry) => entry !== item)
      : [...checked, item]
    setChecked(next)
    emit({ checked: next })
  }

  return (
    <section className={controls.interaction} aria-labelledby={labelId}>
      <p id={labelId} className={controls.prompt}>
        {prompt}
      </p>
      <textarea
        className={styles.textarea}
        aria-labelledby={labelId}
        rows={4}
        value={text}
        onChange={(event) => onText(event.target.value)}
        placeholder="Write your answer…"
      />

      <div className={controls.actions}>
        {model && !revealed && (
          <button type="button" className={controls.button} onClick={reveal}>
            Show model answer
          </button>
        )}
      </div>

      {revealed && model && (
        <div className={styles.model}>
          <p className={styles.modelHeading}>Model answer</p>
          <p className={styles.modelText}>{model}</p>
          {checklist.length > 0 && (
            <>
              <p className={styles.modelHeading}>Did your answer cover these? Check what you got.</p>
              <ul className={styles.checklist}>
                {checklist.map((item) => (
                  <li key={item}>
                    <label className={styles.check}>
                      <input
                        type="checkbox"
                        checked={checked.includes(item)}
                        onChange={() => toggleItem(item)}
                      />
                      <span>{item}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <p className={styles.selfRate}>
                You checked {checked.length} of {checklist.length}. Revise your answer above if
                anything is missing.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  )
}
